import type { Web3DViewer } from '@technology/web3d';
import { getLogger } from '@trimble/logging';
import {
  createEffect,
  createMemo,
  createRoot,
  getOwner,
  on,
  onCleanup,
  Accessor,
} from 'solid-js';
import { CapabilityRegistry } from '../capability-registry/capability-registry';
import { CapabilityRegistryContainer } from '../capability-registry/capability-registry-container';
import { FitToViewBaseCapabilityCoordinator } from './fit-to-view-base-capability-coordinator';
import { LoadableItem, LoadState } from '@trimble/data-loading';
import type { FitToViewOnloadOptions } from './fit-to-view-onload-capability-coordinator.types';

export class FitToViewOnloadCapabilityCoordinator extends FitToViewBaseCapabilityCoordinator {
  private readonly paneNeedsFit: Map<CapabilityRegistryContainer, boolean> =
    new Map();
  private readonly excludedModelIds: ReadonlySet<string>;
  private readonly disposeViewerTracking: () => void;

  constructor(
    registry: CapabilityRegistry,
    dataLoadingService: { items: Accessor<LoadableItem[]> },
    options: FitToViewOnloadOptions = {}
  ) {
    super(registry);
    this.excludedModelIds = new Set(options.excludeModelIds);

    // Initialize all existing panes to need fit
    registry.paneContainers().forEach((pane) => {
      this.paneNeedsFit.set(pane, true);
    });

    const loadStates = createMemo(() =>
      dataLoadingService.items().map((i) => i.loadState)
    );

    // Track when all items are unloaded then reset all panes to need fit
    createEffect(
      on(loadStates, () => {
        const loadItems = dataLoadingService
          .items()
          .filter(
            (i) =>
              i.loadState === LoadState.Loading ||
              i.loadState === LoadState.Reloading ||
              i.loadState === LoadState.LoadComplete
          );

        // When all items are unloaded, mark all panes as needing fit
        if (loadItems.length === 0) {
          registry.paneContainers().forEach((pane) => {
            this.paneNeedsFit.set(pane, true);
          });
        }
      })
    );

    const hasLoadedItems = createMemo(() =>
      dataLoadingService
        .items()
        .some((i) => i.loadState === LoadState.LoadComplete)
    );
    const fitIfLoaded = (): void => {
      if (!hasLoadedItems()) return;
      this.fitPendingPanes();
    };

    // Loaded data can reach a viewer after its item reports LoadComplete, and a
    // viewer can be attached after loading finished. A pending fit has to be
    // retried then, since loadStates will not change again.
    // Owned by a separate root so cleanup() can stop fitting and detach the
    // viewer listeners even when the enclosing owner outlives this coordinator.
    this.disposeViewerTracking = createRoot((dispose) => {
      createEffect(on(loadStates, fitIfLoaded));

      const viewers = createMemo(() =>
        registry.paneContainers().flatMap((pane) => {
          const viewer = pane.viewerWrapper()?.viewer;
          return viewer ? [viewer] : [];
        })
      );

      createEffect(() => {
        for (const viewer of viewers()) {
          viewer.addEventListener('modelload', fitIfLoaded);
          onCleanup(() => viewer.removeEventListener('modelload', fitIfLoaded));
        }
      });

      createEffect(on(viewers, fitIfLoaded));

      return dispose;
    });
    if (getOwner()) {
      onCleanup(this.disposeViewerTracking);
    }
  }

  private fitPendingPanes(): void {
    for (const pane of this.registry.paneContainers()) {
      this.fitPaneIfPending(pane);
    }
  }

  private fitPaneIfPending(pane: CapabilityRegistryContainer): void {
    // Panes added after construction are not in the map yet and still need
    // their first fit.
    if (this.paneNeedsFit.get(pane) === false) return;
    if (!this.isPaneEligibleForFitToView(pane)) return;

    const viewer = pane.viewerWrapper()?.viewer;
    if (!viewer) return;

    const models = this.collectFittableModels(viewer);
    // Excluded models, such as marker icons, can reach the viewer before the
    // loaded data does. Keep the pane marked so the one-time fit lands on the
    // data once it arrives instead of being spent on an empty set.
    if (models.size === 0) return;

    this.paneNeedsFit.set(pane, false);
    viewer.camera.fitToView({ models }).catch((error: unknown) => {
      getLogger().error('Onload fit-to-view failed', error);
    });
  }

  /**
   * Suppresses fit-to-view for a specific pane.
   */
  public suppressFitToView(pane: CapabilityRegistryContainer): void {
    this.paneNeedsFit.set(pane, false);
  }

  public cleanup(): Promise<void> {
    this.disposeViewerTracking();
    this.paneNeedsFit.clear();
    return Promise.resolve();
  }

  private collectFittableModels(viewer: Web3DViewer): Map<string, undefined> {
    const models = new Map<string, undefined>();
    for (const model of viewer.getModels()) {
      if (!this.excludedModelIds.has(model.modelId)) {
        models.set(model.modelId, undefined);
      }
    }
    return models;
  }
}
